// Cards for the unit tools' step results (the Artemis guide's "Step results", API-3885, 3886, 3889
// and 3890): what's happening in a unit, its climate normals, what the weather has actually been
// and how its state draws. Each builder takes a Result and returns a plain object for the card, or
// null when the Result is of another Kind or has nothing to show. Where the Hunt Planner already
// reads the same data (unit-info/unitInfo.js, alertInfo.js) the card goes through its readers.

import { camelKeys } from './keys'
import { alertLabel, asOfText } from '../../component/hunt-planner/huntPlannerComponents/huntPlanner-right-toolbar/unit-info/alertInfo'
import {
  bandDifference, bandLabel, climateBands, climateWind, middleBand, normalsForMonths, normalsText, prevailingWind, skyText
} from '../../component/hunt-planner/huntPlannerComponents/huntPlanner-right-toolbar/unit-info/unitInfo'

const num = v => (typeof v === 'number' && Number.isFinite(v) ? v : null)
const round = v => (num(v) == null ? null : Math.round(v))
const fmt = (v, digits = 0) => {
  const n = num(v)
  return n == null ? null : n.toLocaleString('en-US', { maximumFractionDigits: digits })
}
const pct = v => {
  const n = num(v)
  if (n == null) return null
  return `${n < 1 && n > 0 ? '<1' : Math.round(n)}%`
}
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`
const heading = (...parts) => parts.filter(Boolean).join(' · ')
const sentenceCase = s => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1).toLowerCase() : s)

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const shortDate = ymd => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd || '')
  return m ? `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}` : ''
}
const dateRange = (start, end) => {
  const a = shortDate(start), b = shortDate(end)
  return a && b && a !== b ? `${a} – ${b}` : a || b
}

// get_unit_alerts (API-3885): { title, badge, conditions, news, forum }, each a list of
//   { key, type, label, warning, title, detail, asOf, source, url, layerIds }
// in the server's order (warnings first). Empty Alerts means no source reported anything, which the
// answer says in words, so there's no card for it.
export const unitAlertsCard = result => {
  if (!result || result.Kind !== 'unit_alerts') return null
  const alerts = (Array.isArray(result.Alerts) ? result.Alerts : []).filter(a => a && a.Title)
  if (alerts.length === 0) return null
  const rows = alerts.map((a, i) => ({
    key: `${a.Type || 'alert'}-${i}`,
    type: a.Type || 'alert',
    label: alertLabel(a.Type),
    warning: a.Severity === 'warning',
    title: String(a.Title),
    detail: a.Detail || null,
    asOf: asOfText(a.AsOf) || null,
    source: a.Source || null,
    url: a.Url || null,
    layerIds: (Array.isArray(a.LayerIds) ? a.LayerIds : []).map(Number).filter(id => Number.isInteger(id) && id > 0)
  }))
  const warnings = rows.filter(r => r.warning).length
  return {
    title: heading(result.Unit || 'This unit', 'right now'),
    badge: warnings ? plural(warnings, 'warning') : null,
    conditions: rows.filter(r => r.type !== 'news' && r.type !== 'forum'),
    news: rows.filter(r => r.type === 'news'),
    forum: rows.filter(r => r.type === 'forum')
  }
}

// "Sep – Nov", "Oct", "Sep, Nov", or "all year" for a set of months (1-12).
export const monthsLabel = months => {
  const list = [...months].filter(m => m >= 1 && m <= 12).sort((a, b) => a - b)
  if (list.length === 0) return null
  if (list.length === 12) return 'all year'
  const contiguous = list.every((m, i) => i === 0 || m === list[i - 1] + 1)
  if (list.length > 2 && contiguous) return `${MONTHS[list[0] - 1]} – ${MONTHS[list[list.length - 1] - 1]}`
  return list.map(m => MONTHS[m - 1]).join(', ')
}

// The planner's wind reader keys directions N, NE, …; the card sends them PascalCase (Ne, Sw).
const upperKeys = obj => (obj && typeof obj === 'object' ? Object.fromEntries(Object.entries(obj).map(([k, v]) => [String(k).toUpperCase(), v])) : {})

// get_unit_climate (API-3886): 1991-2020 normals for the months Claude asked for.
//   { title, months (Set), bands: [{ key, label, months }], band (the one to show first),
//     normals: { bandKey: text }, wind, sky, elevation, range, sources: [{ name, url }] }
// bands[].months are the chart's rows ({ month, avgHigh, avgLow, recordHigh, recordLow, precip,
// precipChance, snowChance, snowDepth }); empty Bands means the unit has no climate data.
export const unitClimateCard = result => {
  if (!result || result.Kind !== 'unit_climate') return null
  const asked = new Set((Array.isArray(result.Months) ? result.Months : []).map(Number).filter(m => m >= 1 && m <= 12))
  const bands = climateBands({ bands: (Array.isArray(result.Bands) ? result.Bands : []).map(camelKeys) })
  const wind = climateWind({ monthly: (Array.isArray(result.Monthly) ? result.Monthly : []).map(m => ({ ...camelKeys(m), windDirection: upperKeys(m.WindDirection) })) })
  if (bands.length === 0 && wind.length === 0) return null
  const months = asked.size ? asked : new Set(bands.flatMap(b => b.months.map(m => m.month)))
  const range = result.ElevationRange || {}
  return {
    title: heading(result.Unit || 'Unit', 'usual weather', monthsLabel(months)),
    months,
    bands: bands.map(b => ({ key: b.band, label: bandLabel(b.band), months: b.months })),
    band: middleBand(bands),
    normals: Object.fromEntries(bands.map(b => [b.band, normalsText(normalsForMonths(bands, b.band, months)) || null])),
    wind: prevailingWind(wind, months),
    sky: skyText(wind, months),
    elevation: bandDifference(bands, months),
    range: num(range.Min) != null && num(range.Max) != null ? `${fmt(range.Min)} – ${fmt(range.Max)} ft` : null,
    sources: (Array.isArray(result.Sources) ? result.Sources : []).filter(s => s && s.Name).map(s => ({ name: s.Name, url: s.Url || null }))
  }
}

// A recent figure beside its normal: { label, value, normal, up } or null without the figure.
const figure = (label, value, normal, unit, digits = 0) => {
  const v = num(value)
  if (v == null) return null
  const n = num(normal)
  return {
    label,
    value: `${fmt(v, digits)}${unit}`,
    normal: n != null ? `normal ${fmt(n, digits)}${unit}` : null,
    // Which way it departs from normal, when it does by more than the rounding.
    up: n == null || Math.abs(v - n) < Math.pow(10, -digits) / 2 ? null : v > n
  }
}

const DROUGHT = [['D0', 'Abnormally dry'], ['D1', 'Moderate drought'], ['D2', 'Severe drought'], ['D3', 'Extreme drought'], ['D4', 'Exceptional drought']]

// get_unit_conditions (API-3889): what the weather has been, by elevation band against normal.
//   { title, bands: [{ key, label, figures: [{ label, value, normal, up }], days: [{ key, label, title, high, low, snow, precip }] }],
//     band ('all' first), drought: { asOf, rows: [{ key, label, percent, value }], weeks } | null, through, sources }
export const unitConditionsCard = result => {
  if (!result || result.Kind !== 'unit_conditions') return null
  const bands = (Array.isArray(result.Bands) ? result.Bands : []).filter(b => b && (b.Band || num(b.MinElevation) != null))
  const weeks = (Array.isArray(result.Drought) ? result.Drought : []).filter(w => w && w.ValidDate)
  if (bands.length === 0 && weeks.length === 0) return null
  const rows = bands.map((b, i) => {
    const s = b.Summary && typeof b.Summary === 'object' ? b.Summary : {}
    const whole = !b.Band || String(b.Band).toLowerCase() === 'all'
    const days = Array.isArray(b.Days) ? b.Days.filter(d => d && d.Date) : []
    return {
      key: b.Band || String(i),
      label: whole ? 'Whole unit' : num(b.MinElevation) != null && num(b.MaxElevation) != null ? `${fmt(b.MinElevation)} – ${fmt(b.MaxElevation)} ft` : String(b.Band),
      figures: [
        figure('Highs', s.AvgHigh, s.NormalHigh, '°'),
        figure('Lows', s.AvgLow, s.NormalLow, '°'),
        figure('Precip', s.PrecipTotal, s.NormalPrecip, ' in', 1),
        figure('Snow', s.LatestSnowDepth, s.NormalSnowDepth, ' in')
      ].filter(Boolean),
      weatherDays: num(s.WeatherDays),
      days: days.slice(-7).map(d => ({
        key: d.Date,
        label: String(Number(d.Date.slice(8, 10)) || ''),
        title: [shortDate(d.Date), num(d.High) != null ? `high ${round(d.High)}°` : null, num(d.Low) != null ? `low ${round(d.Low)}°` : null,
          num(d.SnowDepth) != null ? `${fmt(d.SnowDepth, 1)} in snow` : null, num(d.Precip) != null && d.Precip > 0 ? `${fmt(d.Precip, 2)} in precip` : null].filter(Boolean).join(' · '),
        high: round(d.High),
        low: round(d.Low),
        snow: num(d.SnowDepth) != null ? Math.round(d.SnowDepth * 10) / 10 : null,
        precip: num(d.Precip) != null ? Math.round(d.Precip * 100) / 100 : null
      }))
    }
  })
  const latest = weeks.length ? weeks[weeks.length - 1] : null
  const droughtRows = latest
    ? DROUGHT.map(([key, label]) => ({ key, label, percent: Math.max(0, Math.min(100, num(latest[key]) || 0)), value: pct(latest[key]) })).filter(r => r.percent > 0)
    : []
  const weeksIn = num(result.WeeksInDrought)
  return {
    title: heading(result.Unit || 'Unit', 'recent conditions', dateRange(result.From, result.To)),
    bands: rows,
    band: rows.length ? (rows.find(r => r.label === 'Whole unit') || rows[0]).key : null,
    drought: latest && (droughtRows.length || weeksIn)
      ? {
          asOf: shortDate(latest.ValidDate) || null,
          rows: droughtRows,
          weeks: weeksIn ? `${plural(weeksIn, 'week')} running with half the unit in drought` : null
        }
      : null,
    through: [result.SnowThrough && `snow through ${shortDate(result.SnowThrough)}`, result.WeatherThrough && `weather through ${shortDate(result.WeatherThrough)}`].filter(Boolean).join(' · ') || null,
    sources: (Array.isArray(result.Sources) ? result.Sources : []).filter(s => s && s.Name).map(s => ({ name: s.Name, url: s.Url || null }))
  }
}

const MAX_SPECIES = 6

// get_draw_system (API-3890): how a state draws each species.
//   { title, applyUrl, notes, species: [{ key, name, subSpecies, drawType, groups, rule, source }], more }
export const drawSystemCard = result => {
  if (!result || result.Kind !== 'draw_system') return null
  const species = (Array.isArray(result.Species) ? result.Species : []).filter(s => s && (s.Species || s.DrawType || s.Rule))
  if (species.length === 0 && !result.Notes) return null
  return {
    title: heading(result.StateName || result.State || 'State', 'how the draw works'),
    applyUrl: result.ApplicationUrl || null,
    notes: result.Notes || null,
    species: species.slice(0, MAX_SPECIES).map((s, i) => ({
      key: s.Species || String(i),
      name: s.Species ? sentenceCase(s.Species) : 'All species',
      subSpecies: (Array.isArray(s.SubSpecies) ? s.SubSpecies : []).filter(Boolean).join(', ') || null,
      drawType: s.DrawType ? sentenceCase(s.DrawType) : null,
      groups: (Array.isArray(s.Groups) ? s.Groups : []).filter(Boolean).join(', ') || null,
      rule: s.Rule || null,
      source: s.Source || null
    })),
    more: Math.max(0, species.length - MAX_SPECIES)
  }
}

// All the cards above, by Kind: { kind, card } or null.
export const unitCard = result => {
  const builders = { unit_alerts: unitAlertsCard, unit_climate: unitClimateCard, unit_conditions: unitConditionsCard, draw_system: drawSystemCard }
  const build = result && builders[result.Kind]
  const card = build ? build(result) : null
  return card ? { kind: result.Kind, card } : null
}
